import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker } from "@mui/x-date-pickers";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import moment from "moment";
import { companyActions } from "../../../../../../actions";
import validateIncorporationDate from "./validateIncorporationDate";
import QuoteService from "../../../../../../services/quote.service";
import Util from "../../../../../../utils/Util";

const IncorporationDate = ({
  setShowPopupE,
  setShowPopupD,
  onIncorporationDateValidationChange,
  incorporationDatePickerDisabled = false
}) => {
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [validationError, setValidationError] = useState("");
  const [pickerValidationError, setPickerValidationError] = useState(null);

  const validationRequest = useRef(0);
  const lastValidatedDate = useRef(null);
  const tabLoadValidationStarted = useRef(false);

  const quoteId = useSelector(state => state.quote.id);
  const quoteLocked = useSelector(state => state.quote?.locked);

  const incorporationDate = useSelector(
    state => state.company.details?.incorporation_date
  );

  const company = useSelector(state => state.company.details);
  const uen = company?.uen_no;

  const hasDateValue =
    incorporationDate !== undefined &&
    incorporationDate !== null &&
    `${incorporationDate}`.trim() !== "";

  const reduxMoment = hasDateValue
    ? moment(
      Number.isFinite(Number(incorporationDate))
        ? Number(incorporationDate)
        : incorporationDate
    )
    : null;

  const [localDate, setLocalDate] = useState(
    reduxMoment?.isValid() ? reduxMoment : null
  );

  useEffect(() => {
    console.log(
      "[STATE] localDate:",
      localDate?.format?.("DD/MM/YYYY") ?? null
    );
  }, [localDate]);

  useEffect(() => {
    console.log("[STATE] pickerValidationError:", pickerValidationError);
  }, [pickerValidationError]);

  useEffect(() => {
    if (hasDateValue) {
      const parsed = moment(
        Number.isFinite(Number(incorporationDate))
          ? Number(incorporationDate)
          : incorporationDate
      );

      if (
        parsed.isValid() &&
        (!localDate ||
          !localDate.isValid() ||
          localDate.valueOf() !== parsed.valueOf())
      ) {
        setLocalDate(parsed);
      }
    } else if (!hasDateValue && localDate !== null) {
      setLocalDate(null);
    }
  }, [incorporationDate]);

  useEffect(() => {
    return () => {
      validationRequest.current += 1;
      lastValidatedDate.current = null;
    };
  }, [quoteId, uen]);

  const validateApi = async (selectedMoment, requestId) => {
    const startOfDay = selectedMoment.clone().startOf("day");

    console.log(
      "[API] validateIncorporationDate",
      startOfDay.format("YYYY-MM-DD")
    );

    dispatch(
      companyActions.setCompanyFormDetails({
        key: "incorporation_date",
        value: startOfDay.valueOf()
      })
    );

    return validateIncorporationDate({
      quoteId,
      uen,
      incorporationDate: startOfDay.format("YYYY-MM-DD"),
      setShowPopupD,
      setShowPopupE,
      onIncorporationDateValidationChange,
      setValidationError,
      isCurrent: () => requestId === validationRequest.current
    });
  };

  const triggerValidation = (dateValue, source) => {
    console.log(`[VALIDATION] Triggered from ${source}`);

    if (!dateValue) {
      console.log("[VALIDATION] No date");
      return;
    }

    const selectedMoment = moment(dateValue);

    if (!selectedMoment.isValid()) {
      console.log("[VALIDATION] Invalid date");
      return;
    }

    if (pickerValidationError) {
      console.log(
        "[VALIDATION] Picker validation error:",
        pickerValidationError
      );
      return;
    }

    const normalizedDate = selectedMoment
      .clone()
      .startOf("day")
      .valueOf();

    if (lastValidatedDate.current === normalizedDate) {
      console.log(
        "[VALIDATION] Skipped duplicate validation:",
        selectedMoment.format("DD/MM/YYYY")
      );
      return;
    }

    lastValidatedDate.current = normalizedDate;

    const requestId = ++validationRequest.current;

    return validateApi(selectedMoment, requestId);
  };

  useEffect(() => {
    if (tabLoadValidationStarted.current) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      tabLoadValidationStarted.current = true;

      console.log("Submit Documents tab loaded - starting incorporation date validation check");
      console.log("Incorporation Date value:", incorporationDate);
      console.log("UEN value:", uen);

      try {
        if (quoteLocked) {
          console.log("Quote is locked - skipping validation on tab load");
          onIncorporationDatePickerDisabledChange?.(true);
          onIncorporationDateValidationChange?.(false);
        } else {
          if (hasDateValue) {
            await triggerValidation(reduxMoment, "tab load");
          } else {
            console.log("Skipping validation on tab load because Incorporation Date is empty");
          }
        }
      } finally {
        console.log("Tab load validation completed");
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  });

  const handleDateChange = (date, context) => {
    console.log("[PICKER] onChange", {
      date: date?.format?.("DD/MM/YYYY"),
      validationError: context?.validationError
    });

    setLocalDate(date);
    setValidationError("");
    setPickerValidationError(context?.validationError ?? null);

    if (!date) {
      lastValidatedDate.current = null;

      dispatch(
        companyActions.setCompanyFormDetails({
          key: "incorporation_date",
          value: ""
        })
      );

      onIncorporationDateValidationChange?.(false);
    }
  };

  const handleAccept = value => {
    console.log(
      "[PICKER] onAccept",
      value?.format?.("DD/MM/YYYY")
    );

    if (!value) {
      return;
    }

    setLocalDate(value);

    triggerValidation(value, "picker accept");
  };

  const handleBlur = () => {
    console.log("[TEXTFIELD] onBlur");

    const isCurrentlyValid =
      localDate &&
      moment(localDate).isValid() &&
      !pickerValidationError;

    if (!isCurrentlyValid) {
      console.log("[TEXTFIELD] Invalid value - clearing");

      lastValidatedDate.current = null;

      setLocalDate(null);

      dispatch(
        companyActions.setCompanyFormDetails({
          key: "incorporation_date",
          value: ""
        })
      );

      onIncorporationDateValidationChange?.(false);

      return;
    }

    triggerValidation(localDate, "blur");
  };

  const handleGuideClick = async event => {
    event.preventDefault();

    if (downloading) {
      return;
    }

    setDownloading(true);
    setDownloadError("");

    try {
      const response = await QuoteService.getIncorporationDateGuide();

      Util.downloadFile(
        response.data,
        "Incorporation-date.pdf",
        "application/pdf"
      );
    } catch {
      setDownloadError(
        "Unable to download the guide. Please try again."
      );
    } finally {
      setDownloading(false);
    }
  };

  const hasValidDate =
    localDate &&
    moment(localDate).isValid() &&
    !pickerValidationError;

  const isPickerDisabled = quoteLocked || incorporationDatePickerDisabled;
  return (
    <Box sx={{ display: "contents" }}>
      <Link
        component="button"
        type="button"
        onClick={handleGuideClick}
        disabled={downloading}
        aria-busy={downloading}
        aria-describedby={
          downloadError ? "incorporation-guide-error" : undefined
        }
        variant="caption"
        sx={{
          gridColumn: { xs: 1, sm: 2 },
          gridRow: { xs: 2, sm: 1 },
          justifySelf: "start",
          mx: "14px",
          textAlign: "left"
        }}
      >
        Download the guide to find the Incorporation Date
      </Link>

      <DatePicker
        label="Incorporation Date/Date of Establishment"
        open={open}
        onOpen={() => {
          console.log("[PICKER] opened");
          setOpen(true);
        }}
        onClose={() => {
          console.log("[PICKER] closed");
          setOpen(false);
        }}
        onAccept={handleAccept}
        value={localDate}
        disabled={isPickerDisabled}
        format="DD/MM/YYYY"
        onChange={handleDateChange}
        slotProps={{
          textField: {
            sx: {
              gridColumn: { xs: 1, sm: 2 },
              gridRow: { xs: 3, sm: 2 },
              minWidth: 0,
              alignSelf: "start"
            },
            id: "incorporation_date",
            name: "incorporation_date",
            autoComplete: "[off]",
            error: !hasValidDate || Boolean(validationError),
            fullWidth: true,
            helperText: hasValidDate
              ? validationError
              : "Required",
            InputLabelProps: {
              onClick: () => setOpen(true)
            },
            onClick: () => setOpen(true),
            onBlur: handleBlur,
            size: "small"
          }
        }}
      />

      {downloadError && (
        <FormHelperText
          error
          id="incorporation-guide-error"
          role="alert"
          sx={{
            gridColumn: { xs: 1, sm: 2 },
            gridRow: { xs: 4, sm: 3 },
            mx: "14px"
          }}
        >
          {downloadError}
        </FormHelperText>
      )}
    </Box>
  );
};

export default IncorporationDate;